import { useEffect, useId } from 'react';

import { useDocumentTitleStore } from '~/components/v2/store/features/document-title/useDocumentTitleStore';

export type UseDocumentTitleParams = {
  title: string | undefined;
  isFallback: boolean;
  /** The page will have a title once its data loads. Keeps the fallback title from showing in the meantime. */
  isPending?: boolean;
};

/**
 * Registers a tab title with the single `<title>` owner (`DocumentTitleHead` in `_app`).
 * Several next/head instances emitting `<title>` race on every re-render, so callers
 * register here instead. A missing or empty title unregisters, letting a fallback title apply,
 * unless `isPending` is set.
 */
export const useDocumentTitle = ({ title, isFallback, isPending = false }: UseDocumentTitleParams) => {
  const id = useId();
  const setEntry = useDocumentTitleStore((state) => state.setEntry);
  const removeEntry = useDocumentTitleStore((state) => state.removeEntry);

  useEffect(() => {
    if (!title && !isPending) {
      removeEntry(id);
      return;
    }

    setEntry({ id, title: title || undefined, isFallback });
  }, [id, isFallback, isPending, removeEntry, setEntry, title]);

  // Removing only on unmount keeps the entry's position when the title changes to another value,
  // so a page title update does not jump ahead of titles registered after it.
  useEffect(() => () => removeEntry(id), [id, removeEntry]);
};
